import { useEffect, useMemo, useState } from "react";
import { Hooks } from "./hooks";
import { Router } from "./routes/authenticatedRoutes";
import { usePreventZoom } from "./hooks/usePreventZoom";
import "./styles/index.css";
import { useAuthStore } from "./store/useAuthStore";
import { useSocketStore } from "./services/socket-io";
import { useChatStore } from "./store/chatStore";
import { useNotificationBadgeStore } from "./store/useNotificationBadgeStore";
import { GlobalStyles } from "@mui/material";
import { PushNotificationsWrapper } from "./components/PushNotificationsWrapper";

function App() {
  const [showUpdateToast, setShowUpdateToast] = useState(false);

  const chats = useChatStore((s) => s.chats);
  const notifUnread = useNotificationBadgeStore((s) => s.unreadCount);
  const chatUnread = useMemo(
    () => chats.reduce((sum, c) => sum + (c.unreadMessagesCount || 0), 0),
    [chats]
  );

  const syncBadgeWithServiceWorker = (count: number) => {
    if (!("serviceWorker" in navigator)) return;

    const payload = { type: "UPDATE_BADGE", count };
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(payload);
      return;
    }

    navigator.serviceWorker.ready
      .then((registration) => {
        const target = registration.active || registration.waiting || registration.installing;
        target?.postMessage(payload);
      })
      .catch(() => {});
  };

  // Bloquear zoom em toda a aplicação
  usePreventZoom();

  useEffect(() => {
    const { isConnected } = useSocketStore.getState();
    const user = useAuthStore.getState().user;
    
    // Só conecta se não estiver conectado e tiver usuário
    if (!isConnected && user) {
      useSocketStore.getState().connectSocket(user.id, true);
    }
    
    return () => {
      // Socket.io já tem reconexão automática, não precisa desconectar aqui
      // Apenas desconecta se o usuário fizer logout (isso é tratado no socket-io.ts)
    };
  }, []);

  useEffect(() => {
    // 🔁 Marca recarregamento antes de sair
    window.addEventListener("beforeunload", () => {
      sessionStorage.setItem("isReloading", "true");
    });

    // Mantém sessão persistente para permitir push/call ao reabrir o app.
    // O logout explícito continua limpando os dados via fluxo de autenticação.
    sessionStorage.setItem("isReloading", "true");

    // 📩 Listener de mensagens do Service Worker (auto reload com anti-loop)
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type !== "RELOAD_PAGE") return;

        const lastReloadAt = Number(sessionStorage.getItem("sw-reload-at") || "0");
        const now = Date.now();
        // Prevent infinite "Nova versão / atualizando" flicker if SW keeps broadcasting.
        if (now - lastReloadAt < 60_000) {
          console.warn("🟡 RELOAD_PAGE ignorado (cooldown anti-loop)");
          return;
        }

        console.log("🟢 Atualização detectada. Recarregando...");
        sessionStorage.setItem("sw-reload-at", String(now));
        setShowUpdateToast(true);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      });
    }

    // 📦 Registro do Service Worker — único para todos os navegadores (evita conflito de subscriptions)
    window.addEventListener("load", () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker
          .register("/service-worker.js", { scope: "/" })
          .then((registration) => {
            console.log("✅ Service Worker registrado:", registration.scope);
          })
          .catch((err) => {
            console.error("❌ Erro ao registrar o SW:", err);
          });
      }
    });

    const isMobile = window.innerWidth <= 768;
    localStorage.setItem("@backoffice:isMobile", isMobile.toString());
  }, []);

  useEffect(() => {
    // Ícone do app (PWA): mensagens não lidas + notificações do sino (convites etc.)
    const badgeTotal = chatUnread + notifUnread;
    syncBadgeWithServiceWorker(badgeTotal);
    if ("setAppBadge" in navigator) {
      if (badgeTotal > 0) {
        navigator.setAppBadge(badgeTotal).catch(() => {});
      } else if ("clearAppBadge" in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    }
  }, [chatUnread, notifUnread]);

  return (
    <>
      <GlobalStyles
        styles={{
          "html, body, #root": {
            height: "100%",
            width: "100%",
            maxWidth: "none",
            minHeight: "100%",
            margin: 0,
            padding: 0,
            overflow: "hidden",
            position: "fixed",
            inset: 0,
            boxSizing: "border-box",
          },
          "#root": {
            display: "block",
          },
          ".App, [data-testid='messenger-shell']": {
            position: "fixed",
            inset: 0,
            width: "100%",
            height: "100%",
            maxWidth: "none",
            maxHeight: "none",
            margin: 0,
            padding: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          },
        }}
      />

      <Hooks>
        <Router />
        {/* 🔔 Gerencia Web Push: modal de permissão + guia de instalação iOS */}
        <PushNotificationsWrapper />
      </Hooks>

      {showUpdateToast && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white text-gray-800 px-5 py-4 rounded-2xl shadow-xl border border-gray-300 flex items-center gap-4 z-50 animate-fadeIn">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green-500"></div>
          <div className="flex flex-col">
            <span className="font-semibold">Nova versão disponível</span>
            <span className="text-sm text-gray-500">Atualizando automaticamente...</span>
            <div className="w-full h-1 bg-gray-200 rounded mt-2 overflow-hidden">
              <div className="h-full bg-green-500 animate-progressBar"></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
